import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextService } from './Service';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ServiceDA } from '../../../../services/admin/service.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { FeatureDetail } from '../../../../models/feature.detail.model';
import { Message } from '../../../../models/message.model';
import { Service } from '../../../../models/service.model';
import { legendInvalid, legendIconInvalid } from '../../../../utils/legend.data.util';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { uploadUrl } from '../../../../scripts/file.load.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';

import AppServiceForm from './component/ServiceForm';
import AppPreloader from '../../../../components/Preloader';

export interface ServiceUpdatePageProps {};

let errorResponse: Error, messageResponse: Message, serviceResponse: Service;

const ServiceUpdatePage: React.FunctionComponent<ServiceUpdatePageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, loadServicesForCompany} = UseOutletContextService()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [description, setDescription] = useState({value: '', valid: false})
  const [category, setCategory] = useState({value: '', valid: false, object: null as any})
  const [code, setCode] = useState({value: '', valid: false})
  const [sku, setSku] = useState({value: '', valid: false})
  const [codebar, setCodebar] = useState({value: '', valid: false})
  const [priceSale, setPriceSale] = useState({value: '', valid: false})
  const [pricePurchase, setPricePurchase] = useState({value: '', valid: false})
  const [taxCodeSale, setTaxCodeSale] = useState({value: '', valid: false})
  const [taxCodePurchase, setTaxCodePurchase] = useState({value: '', valid: false})
  const [taxRateSale, setTaxRateSale] = useState({value: 0, valid: false})
  const [taxRatePurchase, setTaxRatePurchase] = useState({value: 0, valid: false})
  const [unit, setUnit] = useState({value: '', valid: false, object: null as any})
  const [discount, setDicount] = useState({value: {type: 'none', value: '', quantity: ''}, valid: {type: true, value: true, quantity: true}})
  const [names, setNames] = useState({value: {en: '', fr: '', it: '', de: ''}, valid: {en: true, fr: true, it: true, de: true}})
  const [accounts, setAccounts] = useState({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})
  const [photos, setPhotos] = useState({value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: {gallery: true, list: [] as {file: boolean, message: string}[]}})
  const [features, setFeatures] = useState({value: [] as FeatureDetail[], valid: [] as boolean[]})
  const [tags, setTags] = useState({value: [] as string[], valid: true})
  const [service, setService] = useState<Service | null>(null)

  const loadService = async (id_company: string, id_service: string) => {
    await ServiceDA.getService(id_company, id_service).then( (response) => {
      if (response.status === 200) {
        serviceResponse = response.data?.data ?? response.data

        let tempPhotosValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempPhotosValid: {file: boolean, message: string}[] = []

        for (let photo of serviceResponse.photos) {
          tempPhotosValue.push(uploadUrl(lang, photo).value)
          tempPhotosValid.push(uploadUrl(lang, photo).valid)
        }

        setService(serviceResponse)
        setName({value: serviceResponse.display_name, valid: true})
        setDescription({value: serviceResponse.description, valid: true})
        setCategory({value: typeof serviceResponse.category === 'object' ? (serviceResponse.category?.id ?? '') : (serviceResponse.category ?? ''), valid: true, object: typeof serviceResponse.category === 'object' ? serviceResponse.category : null})
        setCode({value: serviceResponse.code, valid: true})
        setSku({value: serviceResponse.sku, valid: true})
        setCodebar({value: serviceResponse.codebar, valid: true})
        setPriceSale({value: (serviceResponse.price_sale?.amount ?? '').toString(), valid: true})
        setPricePurchase({value: (serviceResponse.price_purchase?.amount ?? '').toString(), valid: true})
        setTaxCodeSale({value: serviceResponse.price_sale?.tax_code ?? '', valid: true})
        setTaxCodePurchase({value: serviceResponse.price_purchase?.tax_code ?? '', valid: true})
        setTaxRateSale({value: serviceResponse.price_sale?.tax_rate ?? 0, valid: true})
        setTaxRatePurchase({value: serviceResponse.price_purchase?.tax_rate ?? 0, valid: true})
        setUnit({value: typeof serviceResponse.unit === 'object' ? (serviceResponse.unit?.id ?? '') : (serviceResponse.unit ?? ''), valid: true, object: typeof serviceResponse.unit === 'object' ? serviceResponse.unit : null})
        setDicount({value: {type: serviceResponse.discount?.type ?? 'none', value: (serviceResponse.discount?.value ?? '').toString(), quantity: (serviceResponse.discount?.quantity ?? '').toString()}, valid: {type: true, value: true, quantity: true}})
        setNames({value: serviceResponse.names, valid: {en: true, fr: true, it: true, de: true}})
        setAccounts({value: serviceResponse.accounts ?? {expense: serviceResponse.expense_account_id ?? '', vat: serviceResponse.tva_account_id ?? '', income: serviceResponse.product_account_id ?? ''}, valid: {expense: true, vat: true, income: true}})
        setPhotos({value: tempPhotosValue, valid: {gallery: true, list: tempPhotosValid}})
        setFeatures({value: serviceResponse.features ?? [], valid: (serviceResponse.features ?? []).map(() => {return true})})
        setTags({value: serviceResponse.tags ?? [], valid: true})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const resolveUnit = (unitId: string): { id: string, type: string, name: string, symbol: string } => {
    const mu = settingsForCompany?.measure_units?.find((m: any) => m.id === unitId)
    return mu
      ? { id: mu.id, type: mu.type ?? 'service', name: mu.name ?? '', symbol: mu.symbol ?? '' }
      : { id: unitId, type: '', name: '', symbol: '' }
  }

  const submitServiceUpdate = async () => {
    setLoadIndicator('on')

    let namesIsValid: boolean = isValidObjectAttributes(names.valid)
    let accountsIsValid: boolean = isValidObjectAttributes(accounts.valid)
    let discountIsValid: boolean = isValidObjectAttributes(discount.valid)
    let photosIsValid: boolean = isValidObjectAttributes(photos.valid.list)
    let featuresIsValid: boolean = features.valid.every((item) => (item === true))

    if (companyForUser && service && name.valid && description.valid && code.valid && sku.valid && codebar.valid && priceSale.valid && pricePurchase.valid && taxCodeSale.valid && taxCodePurchase.valid && taxRateSale.valid && taxRatePurchase.valid && unit.valid && category.valid && tags.valid && discountIsValid && namesIsValid && accountsIsValid && photosIsValid && featuresIsValid) {
      let listPhotos = photos.value.map((sub_item) => (sub_item.file))

      await ServiceDA.putService(companyForUser.id, service.id, name.value, description.value, code.value, sku.value, codebar.value, priceSale.value, pricePurchase.value, taxCodeSale.value, taxCodePurchase.value, taxRateSale.value, taxRatePurchase.value, resolveUnit(unit.value), category.value, discount.value, names.value, accounts.value, features.value, tags.value, listPhotos).then( (response) => {
        if (response.status === 200) { 
          messageResponse = response.data
          loadServicesForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate(-1)
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value) legendIconInvalid('input-name', 'legend-name-required')
        if (!name.value) legendIconInvalid('input-name-other', 'legend-name-other-required')
        if (!category.value) legendInvalid('legend-category-required')
        if (!description.value) legendIconInvalid('input-description', 'legend-description-required')
        if (!unit.value) legendInvalid('legend-unit-required')
        if (!priceSale.value) legendIconInvalid('input-price-sale', 'legend-price-sale-required')
        if (!pricePurchase.value) legendIconInvalid('input-price-purchase', 'legend-price-purchase-required')
        if (!taxCodeSale.value) legendInvalid('legend-tax-sale-required')
        if (!taxCodePurchase.value) legendInvalid('legend-tax-purchase-required')
        if (!accounts.value.expense) legendInvalid('legend-account-expense-required')
        if (!accounts.value.vat) legendInvalid('legend-account-vat-required')
        if (!accounts.value.income) legendInvalid('legend-account-income-required')
        if (discount.value.type !== 'none') {
          if (!discount.value.value) legendIconInvalid('input-discount-value', 'legend-discount-value-required')
          if (!discount.value.quantity) legendIconInvalid('input-discount-quantity', 'legend-discount-quantity-required')
        }
        if (!featuresIsValid) {
          for (let i = 0; i < features.value.length; i++) {
            if (!features.value[i].value) legendInvalid(`legend-feature-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadService(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.services, branch: lang.labels.updateService}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && categoriesForCompany && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany && service
        ?
        <AppServiceForm action={"update"} loadIndicator={loadIndicator} name={name} description={description} category={category} code={code} sku={sku} codebar={codebar} priceSale={priceSale} pricePurchase={pricePurchase} taxCodeSale={taxCodeSale} taxCodePurchase={taxCodePurchase} taxRateSale={taxRateSale} taxRatePurchase={taxRatePurchase} unit={unit} discount={discount} names={names} accounts={accounts} photos={photos} features={features} tags={tags} setName={setName} setDescription={setDescription} setCategory={setCategory} setCode={setCode} setSku={setSku} setCodebar={setCodebar} setPriceSale={setPriceSale} setPricePurchase={setPricePurchase} setTaxCodeSale={setTaxCodeSale} setTaxCodePurchase={setTaxCodePurchase} setTaxRateSale={setTaxRateSale} setTaxRatePurchase={setTaxRatePurchase} setUnit={setUnit} setDicount={setDicount} setNames={setNames} setAccounts={setAccounts} setPhotos={setPhotos} setFeatures={setFeatures} setTags={setTags} submitService={submitServiceUpdate}></AppServiceForm>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default ServiceUpdatePage;
